import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  TableSortLabel,
  LinearProgress,
} from "@mui/material";
import {
  DirectionsCar,
  LocalGasStation,
  TrendingUp,
  TrendingDown,
  Download,
  Info,
  Speed,
  AttachMoney,
  GpsFixed,
  Assessment,
  DateRange,
  LocationOn,
} from "@mui/icons-material";
import NavigationHeader from "../../components/NavigationHeader";
import FuelReportsFilters from "../../components/fuel-reports/FuelReportsFilters";
import { formatNumber, formatCurrency } from "../../utils/formatters";
import { useResponsiveBreakpoints } from "../../utils/responsiveUtils";
import {
  commonTypographyProps,
  commonButtonProps,
  tableHeaderStyles,
  tableCellStyles,
  tableRowStyles,
  commonTableContainerProps,
} from "../../utils/styles";
import { useQuery } from "@tanstack/react-query";
import { FuelReportsService } from "../../services/fuelReportsService";
import {
  FuelReportsProvider,
  useFuelReports,
} from "../../contexts/FuelReportsContext";

// Type definitions based on response.json structure
interface VehicleData {
  vehicle_id: string;
  vehicleNumber: string;
  model: string;
  vehicleType: string;
  makeYear: number | null;
  registrationDate: string | null;
  vehicle_age: number;
  total_km_driven: string;
  gps_record_count: number;
  avg_daily_km: string;
  base_location: string;
  gps_district: string;
  vehicle_category: string;
  first_record_date: string;
  last_record_date: string;
  total_fuel_transactions: string;
  total_fuel_cost: string;
  total_fuel_liters: string;
  avg_price_per_liter: string;
  fuel_sources: string | null;
  first_fuel_date: string | null;
  last_fuel_date: string | null;
  fuel_efficiency_kmpl: string | null;
  cost_per_km: string | null;
  fuel_consumption_per_100km: string | null;
  efficiency_rating: string;
  data_status: string;
}

interface EfficiencyDistribution {
  excellent: number;
  good: number;
  average: number;
  poor: number;
  insufficient_data: number;
}

interface DataCoverage {
  complete_data_percentage: string;
  gps_coverage_percentage: string;
  fuel_coverage_percentage: string;
}

interface Summary {
  total_vehicles: number;
  vehicles_with_complete_data: number;
  vehicles_with_gps_only: number;
  vehicles_with_fuel_only: number;
  total_km_driven: number;
  total_fuel_consumed: number;
  total_fuel_cost: number;
  avg_efficiency: string;
  efficiency_distribution: EfficiencyDistribution;
  data_coverage: DataCoverage;
}

interface ReportData {
  isSuccess: boolean;
  message: string;
  data: {
    summary: Summary;
    data: VehicleData[];
  };
}

type SortOrder = "asc" | "desc";
type SortField = keyof VehicleData | "";

const VehicleGPSFuelReportContent: React.FC = () => {
  const { filters } = useFuelReports();
  const { isMobile, isTablet } = useResponsiveBreakpoints();
  const [sortField, setSortField] = useState<SortField>("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  // Fetch vehicle consumption data
  const {
    data: reportData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["vehicle-gps-fuel-report", filters],
    queryFn: () =>
      FuelReportsService.getVehicleReportBasedOnFuelConsumptionAndTotalDistanceTraveled(
        filters
      ),
    enabled: Boolean(filters.dateFilter),
  });

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const sortedData = React.useMemo(() => {
    if (!reportData?.data?.data || !sortField)
      return reportData?.data?.data || [];

    return [...reportData.data.data].sort((a, b) => {
      const aVal = a[sortField as keyof VehicleData];
      const bVal = b[sortField as keyof VehicleData];

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });
  }, [reportData?.data?.data, sortField, sortOrder]);

  const handleExport = async () => {
    try {
      const blob = await FuelReportsService.exportReport({
        ...filters,
        reportType: "vehicle-gps-fuel",
      });
      FuelReportsService.downloadCSV(blob, "vehicle-gps-fuel-report.csv");
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const getDataStatusColor = (status: string) => {
    if (status === "Complete Data") return "#4CAF50";
    if (status === "GPS Only") return "#FF9800";
    if (status === "Fuel Only") return "#2196F3";
    return "#9E9E9E";
  };

  const getEfficiencyColor = (rating: string) => {
    if (rating.includes("Excellent")) return "#4CAF50";
    if (rating.includes("Good")) return "#8BC34A";
    if (rating.includes("Average")) return "#FF9800";
    if (rating.includes("Poor")) return "#F44336";
    return "#9E9E9E";
  };

  const renderSummaryCards = () => {
    if (!reportData?.data?.summary) return null;

    const { summary } = reportData.data;

    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1.5,
              }}
            >
              <Avatar sx={{ bgcolor: "#2196F3", width: 40, height: 40 }}>
                <DirectionsCar sx={{ fontSize: "1rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "1rem" }}
                >
                  {formatNumber(summary.total_vehicles)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Total Vehicles
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1.5,
              }}
            >
              <Avatar sx={{ bgcolor: "#4CAF50", width: 40, height: 40 }}>
                <GpsFixed sx={{ fontSize: "1rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "1rem" }}
                >
                  {formatNumber(summary.total_km_driven)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Total KM Driven
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1.5,
              }}
            >
              <Avatar sx={{ bgcolor: "#FF9800", width: 40, height: 40 }}>
                <LocalGasStation sx={{ fontSize: "1rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "1rem" }}
                >
                  {formatNumber(summary.total_fuel_consumed, 1)}L
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Total Fuel Consumed
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 1.5,
              }}
            >
              <Avatar sx={{ bgcolor: "#9C27B0", width: 40, height: 40 }}>
                <AttachMoney sx={{ fontSize: "1rem" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 0.25, fontSize: "1rem" }}
                >
                  {formatCurrency(summary.total_fuel_cost)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Total Fuel Cost
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading vehicle GPS fuel report: {error?.message}
        <Button onClick={() => refetch()} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Data Table */}
      <Card>
        <CardContent sx={{ p: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              flexDirection: isMobile ? "column" : "row",
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "0.9rem", fontWeight: "bold" }}
            >
              Vehicle Details ({sortedData?.length} vehicles)
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              {...commonButtonProps}
            >
              Export CSV
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: isMobile ? 400 : 600,
              ...commonTableContainerProps,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ ...tableRowStyles }}>
                  <TableCell sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "vehicleNumber"}
                      direction={
                        sortField === "vehicleNumber" ? sortOrder : "asc"
                      }
                      onClick={() => handleSort("vehicleNumber")}
                      sx={{ ...commonTypographyProps }}
                    >
                      Vehicle
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ ...tableHeaderStyles }}>
                      <TableSortLabel
                        active={sortField === "gps_district"}
                        direction={
                          sortField === "gps_district" ? sortOrder : "asc"
                        }
                        onClick={() => handleSort("gps_district")}
                        sx={{ ...commonTypographyProps }}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "total_km_driven"}
                      direction={
                        sortField === "total_km_driven" ? sortOrder : "asc"
                      }
                      onClick={() => handleSort("total_km_driven")}
                      sx={{ ...commonTypographyProps }}
                    >
                      KM Driven
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "avg_daily_km"}
                      direction={
                        sortField === "avg_daily_km" ? sortOrder : "asc"
                      }
                      onClick={() => handleSort("avg_daily_km")}
                      sx={{ ...commonTypographyProps }}
                    >
                      Avg Daily KM
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    Fuel Efficiency
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    Data Status
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                        GPS Records
                      </TableCell>
                      <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                        Fuel Transactions
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(sortedData as VehicleData[]).map(
                  (vehicle: VehicleData, index: number) => (
                    <TableRow
                      key={`${vehicle.vehicleNumber}-${index}`}
                      hover
                      sx={{ ...tableRowStyles }}
                    >
                      <TableCell sx={{ ...tableCellStyles }}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "medium",
                              ...commonTypographyProps,
                            }}
                          >
                            {vehicle.vehicleNumber}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {vehicle.model} • {vehicle.vehicleType}
                          </Typography>
                          {isMobile && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {vehicle.gps_district}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ ...tableCellStyles }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ ...commonTypographyProps }}
                            >
                              {vehicle.gps_district}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {vehicle.base_location?.length > 25
                                ? `${vehicle.base_location.substring(0, 25)}...`
                                : vehicle.base_location}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ ...tableCellStyles }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "medium",
                            ...commonTypographyProps,
                          }}
                        >
                          {formatNumber(parseInt(vehicle.total_km_driven))} km
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ ...tableCellStyles }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "medium",
                            ...commonTypographyProps,
                          }}
                        >
                          {formatNumber(parseFloat(vehicle.avg_daily_km), 1)} km
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ ...tableCellStyles }}>
                        {vehicle.fuel_efficiency_kmpl ? (
                          <Tooltip title={vehicle.efficiency_rating}>
                            <Chip
                              label={`${formatNumber(
                                parseFloat(vehicle.fuel_efficiency_kmpl),
                                1
                              )} km/l`}
                              size="small"
                              sx={{
                                bgcolor: getEfficiencyColor(
                                  vehicle.efficiency_rating
                                ),
                                color: "white",
                                fontWeight: "medium",
                                ...commonTypographyProps,
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            No data
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ ...tableCellStyles }}>
                        <Chip
                          label={vehicle.data_status}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: getDataStatusColor(
                              vehicle.data_status
                            ),
                            color: getDataStatusColor(vehicle.data_status),
                            fontWeight: "medium",
                            ...commonTypographyProps,
                          }}
                        />
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell align="center" sx={{ ...tableCellStyles }}>
                            <Typography
                              variant="body2"
                              sx={{ ...commonTypographyProps }}
                            >
                              {vehicle.gps_record_count}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ ...tableCellStyles }}>
                            <Typography
                              variant="body2"
                              sx={{ ...commonTypographyProps }}
                            >
                              {vehicle.total_fuel_transactions}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {sortedData.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ ...commonTypographyProps }}
              >
                No vehicle data found.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const VehicleGPSFuelReport: React.FC = () => {
  return (
    <FuelReportsProvider>
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <NavigationHeader
            title="Vehicle GPS & Fuel Consumption Report"
            backNavigationEnabled={true}
          />

          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ ...commonTypographyProps }}>
                This comprehensive report combines GPS tracking data with fuel
                consumption metrics to provide insights into vehicle efficiency,
                location tracking, and operational performance across your
                fleet.
              </Typography>
            </Box>
          </Alert>

          <FuelReportsFilters onApplyFilters={() => {}} />

          <VehicleGPSFuelReportContent />
        </Box>
      </Container>
    </FuelReportsProvider>
  );
};

export default VehicleGPSFuelReport;
